import React from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { AutocompleteRenderOptionState } from '@material-ui/lab/Autocomplete';
import { useApi } from '@backstage/core-plugin-api';
import {
  DefaultEntityFilters,
  EntityAutocompletePicker,
  EntityFilter,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';

/** The field the catalog indexes the domain of a platform service under. */
export const DOMAIN_FIELD = 'spec.domain';

/** The field the catalog indexes the system of a platform service under. */
export const SYSTEM_FIELD = 'spec.system';

/**
 * Narrows a listing of platform services to those of the domains picked.
 *
 * @public
 */
export class EntityDomainFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return { [DOMAIN_FIELD]: this.values };
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

/**
 * Narrows a listing of platform services to those of the systems picked.
 *
 * @public
 */
export class EntitySystemFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return { [SYSTEM_FIELD]: this.values };
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

/** The filters of the listing of platform services. */
export type PlatformServiceEntityFilters = DefaultEntityFilters & {
  domains?: EntityDomainFilter;
  systems?: EntitySystemFilter;
};

/** The titles of the platform domains and systems, by name. */
export type PlatformStructureTitles = {
  domains: Record<string, string | undefined>;
  systems: Record<string, string | undefined>;
};

/**
 * The titles of the platform domains and systems. Services name theirs by
 * name only, so whatever shows one to a reader looks its title up here.
 */
export const usePlatformStructureTitles = (): PlatformStructureTitles => {
  const catalogApi = useApi(catalogApiRef);
  const { value } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: ['Domain', 'System'], 'spec.type': 'platform' },
      fields: ['kind', 'metadata.name', 'metadata.title'],
    });
    const titlesOf = (kind: string) =>
      Object.fromEntries(
        items
          .filter(item => item.kind === kind)
          .map(item => [item.metadata.name, item.metadata.title]),
      ) as Record<string, string | undefined>;
    return { domains: titlesOf('Domain'), systems: titlesOf('System') };
  }, [catalogApi]);
  return value ?? { domains: {}, systems: {} };
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/** The filters that decide which domains and systems are offered. */
const FILTERS_FOR_AVAILABLE_VALUES: Array<keyof PlatformServiceEntityFilters> =
  ['kind', 'type'];

/**
 * A picker of names the catalog holds in `path`, each shown by its title.
 */
const TitledPicker = (props: {
  label: string;
  name: 'domains' | 'systems';
  path: string;
  Filter: typeof EntityDomainFilter | typeof EntitySystemFilter;
  titles: Record<string, string | undefined>;
}) => {
  const { label, name, path, Filter, titles } = props;
  const optionLabel = (value: string) => titles[value] ?? value;

  return (
    <EntityAutocompletePicker<PlatformServiceEntityFilters, typeof name>
      label={label}
      name={name}
      path={path}
      Filter={Filter}
      filtersForAvailableValues={FILTERS_FOR_AVAILABLE_VALUES}
      // The stock option renderer prints the raw facet value and ignores
      // `getOptionLabel`, so the dropdown needs its own row to show the name.
      getOptionLabel={optionLabel}
      renderOption={(
        option: string,
        { selected }: AutocompleteRenderOptionState,
      ) => (
        <FormControlLabel
          control={
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
            />
          }
          label={optionLabel(option)}
          onClick={event => event.preventDefault()}
        />
      )}
    />
  );
};

/** Picks the domains whose platform services are listed. */
export const DomainPicker = (props: {
  titles: Record<string, string | undefined>;
}) => (
  <TitledPicker
    label="Domain"
    name="domains"
    path={DOMAIN_FIELD}
    Filter={EntityDomainFilter}
    titles={props.titles}
  />
);

/** Picks the systems whose platform services are listed. */
export const SystemPicker = (props: {
  titles: Record<string, string | undefined>;
}) => (
  <TitledPicker
    label="System"
    name="systems"
    path={SYSTEM_FIELD}
    Filter={EntitySystemFilter}
    titles={props.titles}
  />
);
